import { call, put, takeLatest } from "redux-saga/effects";
import { IBaseResponse } from "../../../interfaces/base";
import {
  ICreateRecipeAction,
  IDeleteRecipeAction,
  IGetRecipeAction,
  IUpdateRecipeAction,
} from "../../../interfaces/store/recipe-interface";
import {
  getRecipeFailed,
  getRecipeSuccess,
  getRecipeStart,
  createRecipeStart,
  createRecipeSuccess,
  createRecipeFailed,
  updateRecipeStart,
  updateRecipeSuccess,
  updateRecipeFailed,
  deleteRecipeStart,
  deleteRecipeFailed,
  deleteRecipeSuccess,
} from "./recipe-slice";
import {
  deleteRecipeApi,
  getRecipeApi,
  updateRecipeApi,
} from "../../../api/recipes";
import { createRecipeApi } from "../../../api/recipes";
import { IUploadFileParams } from "../../../interfaces/files/files";
import { uploadImage } from "../../utils/fileUpload";
import { base64ToBlob } from "../../../utils/utils";
import { v4 as uuidv4 } from "uuid";

function* _getRecipe({ payload }: IGetRecipeAction) {
  try {
    const { recipeId } = payload;

    if (!recipeId) {
      yield put(getRecipeFailed({ message: "Recipe ID is required" }));
      return;
    }

    const recipe: IBaseResponse = yield call(getRecipeApi, recipeId);

    if (recipe.error) {
      yield put(getRecipeFailed(recipe.error));
    }

    yield put(getRecipeSuccess(recipe.data));
  } catch (error: any) {
    yield put(getRecipeFailed({ message: error.message }));
  }
}

function* _createRecipe({ payload }: ICreateRecipeAction) {
  try {
    const response: IBaseResponse = yield call(createRecipeApi, payload);

    const { imageData, largeImageData } = payload;

    const blobImageData = imageData
      ? {
          ...imageData,
          binaryFile: base64ToBlob(imageData.binaryFile),
        }
      : undefined;

    const blobLargeImageData = largeImageData
      ? {
          ...largeImageData,
          binaryFile: base64ToBlob(largeImageData.binaryFile),
        }
      : undefined;

    yield call(handleMediaData, blobImageData, response.data._id, "outerCover");
    yield call(
      handleMediaData,
      blobLargeImageData,
      response.data._id,
      "innerCover"
    );

    if (response.error) {
      yield put(createRecipeFailed(response.error));
    } else {
      yield put(createRecipeSuccess(response.data));
    }
  } catch (error: any) {
    console.log("error:", error);
    yield put(createRecipeFailed({ message: error.message }));
  }
}

function* _updateRecipe({ payload }: IUpdateRecipeAction) {
  try {
    const { recipeId, recipe, imageData, largeImageData } = payload;

    const blobImageData = imageData
      ? {
          ...imageData,
          binaryFile: base64ToBlob(imageData.binaryFile),
        }
      : undefined;

    const blobLargeImageData = largeImageData
      ? {
          ...largeImageData,
          binaryFile: base64ToBlob(largeImageData.binaryFile),
        }
      : undefined;

    const uuidOuter = uuidv4();
    const uuidInner = uuidv4();
    if (blobImageData && blobImageData.binaryFile) {
      yield call(uploadImage, blobImageData, recipeId, uuidOuter);
    }
    if (blobLargeImageData && blobLargeImageData.binaryFile) {
      yield call(uploadImage, blobLargeImageData, recipeId, uuidInner);
    }

    const updateBody = recipe;

    if (updateBody) {
      if (blobImageData?.fileName) {
        updateBody.outerCover =
          recipeId + "/" + uuidOuter + blobImageData.fileName;
      } else if (blobImageData && blobImageData.binaryFile === null) {
        updateBody.outerCover = null;
      }
      if (blobLargeImageData?.fileName) {
        updateBody.innerCover =
          recipeId + "/" + uuidInner + blobLargeImageData.fileName;
      } else if (blobLargeImageData && blobLargeImageData.binaryFile === null) {
        updateBody.innerCover = null;
      }
    }

    const response: IBaseResponse = yield call(updateRecipeApi, {
      recipeId,
      recipe,
    });

    if (response.error) {
      yield put(updateRecipeFailed(response.error));
    } else {
      yield put(updateRecipeSuccess(response.data));
    }
  } catch (error: any) {
    console.log("error:", error);
    yield put(updateRecipeFailed({ message: error.message }));
  }
}

function* _deleteRecipe({ payload }: IDeleteRecipeAction) {
  try {
    const response: IBaseResponse = yield call(
      deleteRecipeApi,
      payload.recipeId
    );

    if (response.error) {
      yield put(deleteRecipeFailed(response.error));
    } else {
      yield put(deleteRecipeSuccess(response.data));
    }
  } catch (error: any) {
    console.log("error:", error);
    yield put(deleteRecipeFailed({ message: error.message }));
  }
}

function* handleMediaData(
  mediaData: IUploadFileParams,
  responseId: string,
  fieldName: "image" | "video" | "outerCover" | "innerCover"
) {
  const uuid = uuidv4();
  if (mediaData && responseId) {
    if (mediaData.binaryFile) {
      yield call(uploadImage, mediaData, responseId, uuid);
    }
    yield put(
      updateRecipeStart({
        recipe: {
          [fieldName]: mediaData.binaryFile
            ? responseId + "/" + uuid + mediaData.fileName
            : null,
        },
        recipeId: responseId,
      })
    );
  }
}

function* recipeSaga() {
  yield takeLatest(getRecipeStart.type, _getRecipe);
  yield takeLatest(createRecipeStart.type, _createRecipe);
  yield takeLatest(updateRecipeStart.type, _updateRecipe);
  yield takeLatest(deleteRecipeStart.type, _deleteRecipe);
}

export default recipeSaga;
